import { FC, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { Notification } from "../../../types/global/types"
import { actions } from "../../../backend/store/reducers/notificationsReducer"
import { useTranslation } from "../../../hooks/useTranslation"
import "./styles.scss"
import happyReactionIcon from '../../../assets/fonts/happyReaction.svg';
import rudeReactionIcon from '../../../assets/fonts/rudeReaction.svg';
import arrowRightIcon from "../../../assets/fonts/arrow-right.svg";
import closeIcon from "../../../assets/fonts/close.svg"
import { NotificationTypes } from "../NotificationTypes/NotificationTypes";

type Props = {
  notification: Notification,
  markAsRead: (id: string) => void,
  deleteNotification: (id: string) => void,
  onSelectNotification: (notification: Notification) => void,
}

export const NotificationItem: FC<Props> = ({
  notification,
  markAsRead,
  deleteNotification,
  onSelectNotification,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { p } = useTranslation("notifications")

  const handleMarkAsRead = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    markAsRead(notification.id)
  }

  const handleDeleteNotification = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    deleteNotification(notification.id)
  }

  const handleSelectNotification = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    onSelectNotification(notification)
  }

  // Jump straight to the household edit page without first opening the
  // notification detail view (item 2: one-click instead of two).
  const handleGoToHousehold = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch(actions.setVisibilityNotificationModal(false))
    navigate(`/households/update/household-info/${notification.household_id}?tab=info`)
  }

  const isImportedAddressNotFound =
    notification.type === NotificationTypes.ImportedAddressNotFoundNotification &&
    !!notification.household_id

  const isAddressesNeedAttention =
    notification.type === NotificationTypes.AddressesNeedAttentionNotification

  /**
   * The point of the one-per-run summary: it says HOW MANY, and this opens the list of
   * WHICH — Contacts filtered to exactly those households.
   *
   * An inline row action, like "Edit contact" above, rather than a link buried inside the
   * sentence. The first version put "Show them" in the title text, where the list row
   * renders the title as a flat string and it was not clickable at all.
   *
   * Not the map: that carries a great deal of other data and is a view, whereas this is a
   * work queue.
   */
  const handleShowNeedsAttention = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    dispatch(actions.setVisibilityNotificationModal(false))
    navigate((notification.title as any)?.path || "/households?address_needs_attention=1")
  }

  // The list-row title sentence is built by the backend in English. Rebuild it
  // on the frontend from a per-type i18n template so it follows the viewer's
  // chosen language. The dynamic middle piece (household/contact name, file
  // name) comes from `title.householdName`/`linkText` and is kept verbatim.
  const displayTitle = useMemo(() => {
    const t = notification.title ?? {};
    const name = t.householdName ?? t.linkText ?? "";
    // English fallback: the original startOfTitle + name + endOfTitle concat.
    const fallback = `${t.startOfTitle ?? ""}${t.householdName ?? ""}${t.endOfTitle ?? ""}`;

    switch (notification.type) {
      case NotificationTypes.ExtremeReactionNotification:
        return p("nt_extreme_reaction", { name });
      case NotificationTypes.AddressVerificationNotification:
        // Two backend variants, distinguished by the visit's error option.
        return notification.address_error_options === "Contact not at address"
          ? p("nt_contact_not_at_address", { name })
          : p("nt_address_not_found", { name });
      case NotificationTypes.ChangedContactInfoNotification:
        return p("nt_changed_contact_info", { name });
      case NotificationTypes.NewContactNotification:
        return p("nt_new_contact", { name });
      case NotificationTypes.AttemptedThreeTimesNotification:
        return p("nt_attempted_3_times", { name });
      case NotificationTypes.RemindMeNotification:
        return p("nt_remind_me", { name });
      case NotificationTypes.NewHouseholdAddressNotification:
        return notification.isLeftTown
          ? p("nt_left_town", { name })
          : p("nt_home_details", { name });
      case NotificationTypes.ImportCompletedNotification:
        return p("nt_import_completed", { name });
      case NotificationTypes.ImportAbortedNotification:
        return p("nt_import_aborted", { name });
      case NotificationTypes.AddressesNeedAttentionNotification:
        // The backend already composes the count into startOfTitle ("127 addresses
        // couldn't be placed on the map. "). The list row is a one-liner, so it drops
        // the "Show them" link deliberately — that belongs in the detail view, where it
        // can actually be clicked.
        return (t.startOfTitle ?? fallback).trim();
      case NotificationTypes.PendingHouseholdNotification:
        return p("nt_pending_household", { name });
      // Already-translated short labels for the failed route/batch rows.
      case NotificationTypes.RouteDivisionFailedNotification:
        return p("route_division_failed");
      case NotificationTypes.RoutesBatchCreationFailedNotification:
        return p("routes_batch_failed");
      // Route/batch summaries: compose the count fragments the API now sends in
      // `title_params` so the whole sentence follows the viewer's language.
      case NotificationTypes.RouteDivisionCompletedNotification: {
        const tp = notification.title_params;
        if (!tp) return fallback;
        const parts = [p("nt_routes_created", { n: tp.created ?? 0 })];
        if (tp.excluded) parts.push(p("nt_routes_excluded", { n: tp.excluded }));
        if (tp.skipped) parts.push(p("nt_routes_skipped", { n: tp.skipped }));
        if (tp.unoptimized) parts.push(p("nt_routes_unoptimized", { n: tp.unoptimized }));
        return parts.join(", ");
      }
      case NotificationTypes.RoutesBatchCreatedNotification: {
        const tp = notification.title_params;
        if (!tp) return fallback;
        let s = p("nt_routes_created", { n: tp.created ?? 0 });
        const opt: string[] = [];
        if (tp.optimized) opt.push(p("nt_routes_optimized", { n: tp.optimized }));
        if (tp.unoptimized) opt.push(p("nt_routes_unoptimized", { n: tp.unoptimized }));
        if (opt.length) s += ` (${opt.join(", ")})`;
        if (tp.skipped) s += `, ${p("nt_routes_skipped", { n: tp.skipped })}`;
        return s;
      }
      // Imported-address rows: the address string now rides in `title_params`.
      case NotificationTypes.ImportedAddressNotFoundNotification: {
        const tp = notification.title_params;
        if (!tp) return fallback;
        return tp.approximate
          ? p("nt_import_addr_approximate", { address: tp.address ?? "", name })
          : p("nt_import_addr_not_found", { address: tp.address ?? "", name });
      }
      // Fix round 1 (volunteering-join report): singular/plural per
      // `title_params.count`, the language-agnostic centre names the backend
      // sends -- see DatabaseNotificationResource::resolveTitleParams().
      case NotificationTypes.VolunteerOffersCancelledNotification: {
        const tp = notification.title_params;
        // Fix round 2: guard non-empty too, not just truthy -- not currently
        // reachable (the backend only ever creates this notification when at
        // least one centre was cancelled), but `tp.centres[0]` on an empty
        // array is `undefined`, which would otherwise render as a blank name
        // rather than falling back to the backend's own sentence.
        if (!tp || !tp.centres || tp.centres.length === 0) return fallback;
        return tp.count === 1
          ? p("nt_volunteer_offer_removed", { name: tp.centres[0] })
          : p("nt_volunteer_offers_removed", { count: tp.count ?? tp.centres.length, names: tp.centres.join(", ") });
      }
      default:
        return fallback;
    }
  }, [notification, p]);

  return (
    <div
      className={`notification-item ${
        !notification.is_read ? "notification-item_unread" : ""
      }`}
    >
      {!notification.is_read && (
        <div className="notification-item__marked" 
        onClick={handleMarkAsRead}
      />
      )}
      <div className="notification-item__delete" 
        onClick={handleDeleteNotification}
      >
        <img src={closeIcon} alt={'closeIconFont'} />
      </div>
      { notification.type === NotificationTypes.ExtremeReactionNotification && (
        <div className="notification-item__reaction">
          { notification.reaction_scale === 1 
            && <img src={happyReactionIcon} alt={'happyReactionIcon'} />
          }
          { notification.reaction_scale === 5 
            && <img src={rudeReactionIcon} alt={'rudeReactionIcon'} />
          }
        </div>
      )}
      
      <div className="notification-item__content">
        <h3>
          {displayTitle}
        </h3>
        <div className="notification-item__time">
          {notification.created_at_formatted}
        </div>
        {isImportedAddressNotFound && (
          <button
            type="button"
            className="notification-item__action"
            onClick={handleGoToHousehold}
          >
            {p("edit_contact")}
          </button>
        )}
        {isAddressesNeedAttention && (
          <button
            type="button"
            className="notification-item__action"
            onClick={handleShowNeedsAttention}
          >
            {p("show_addresses_needing_attention")}
          </button>
        )}
      </div>
      <div
        className="notification-item__arrow"
        onClick={handleSelectNotification}
      >
        <img src={arrowRightIcon} alt={'arrowRightIcon'} />
      </div>
    </div>
  )
}
